import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import TransferSdk, { Transfer } from "@smash-sdk/transfer/07-2022";
import { AsyncState } from '../../shared/models/async.interface';


export interface TransferState {
    list: ListTransferState;
    transferDetails: TransferDetails;
    transferFiles: TransferFiles;
    deletedTransfer: DeletedTransferState;
}

export interface TransferFiles extends AsyncState {
    files: TransferSdk.ListTransferFilesOutput['files'];
    next?: string;
    current: string;
    previous: string[];
}

export interface ListTransferState extends AsyncState {
    transfers: TransferSdk.ListTransfersOutput['transfers']
    next?: string;
    current: string;
    previous: string[];
}

export interface TransferDetails extends AsyncState {
    transfer?: TransferSdk.GetTransferOutput['transfer'];
}

export interface DeletedTransferState extends AsyncState {
    transfer?: TransferSdk.GetTransferOutput['transfer'];
}


export const listTransfer = createAsyncThunk(
    'transfer/listTransfer',
    async ({ limit = 10, sort = "desc", start, hasPrevious = false, hadNext = false }: TransferSdk.ListTransfersInput & { hasPrevious?: boolean, hadNext?: boolean }, thunkApi) => {
        try {
            const transfer = new Transfer();
            const { transfers, next } = await transfer.listTransfers({ limit, sort, start });
            return { transfers, next, hasPrevious, hadNext };
        } catch (error) {
            throw error;
        }
    }
)

export const getTransferDetails = createAsyncThunk(
    'transfer/getTransferDetails',
    async ({ transferId }: TransferSdk.GetTransferInput, thunkApi) => {
        try {
            const transfer = new Transfer();
            return await transfer.getTransfer({ transferId });
        } catch (error) {
            throw error;
        }
    }
)

export const listTransferFiles = createAsyncThunk(
    'transfer/listTransferFiles',
    async ({ transferId, limit = 10, sort = "desc", start, hasPrevious = false, hadNext = false }: TransferSdk.ListTransferFilesInput & { hasPrevious?: boolean, hadNext?: boolean }, thunkApi) => {
        try {
            const transfer = new Transfer();
            const { files, next } = await transfer.listTransferFiles({ transferId, limit, sort, start });
            return { files, next, hasPrevious, hadNext };
        } catch (error) {
            throw error;
        }
    }
)


export const deleteTransfer = createAsyncThunk(
    'transfer/deleteTransfer',
    async ({ transferId }: TransferSdk.DeleteTransferInput, thunkApi) => {
        try {
            const transfer = new Transfer();
            return await transfer.deleteTransfer({ transferId });
        } catch (error) {
            throw error;
        }
    }
)



export const initialState: TransferState = {
    list: {
        transfers: [],
        current: 'firstPage',
        previous: [],
        isLoading: false,
        isSuccess: false,
        isError: false,
        error: undefined,
    },
    transferDetails: {
        isSuccess: false,
        isLoading: false,
        isError: false,
    },
    transferFiles: {
        files: [],
        current: 'firstPage',
        previous: [],
        isSuccess: false,
        isLoading: false,
        isError: false,
        error: undefined,
    },
    deletedTransfer: {
        isSuccess: false,
        isLoading: false,
        isError: false,
    }
}

export const transferSlice = createSlice({
    name: 'transfer',
    initialState,
    reducers: {
        resetTransfer: (state) => ({
            ...state, list: {
                transfers: [],
                current: 'firstPage',
                previous: [],
                isLoading: false,
                isSuccess: false,
                isError: false,
                error: undefined,
            }
        }),
        resetTransferFiles: (state) => ({
            ...state, transferFiles: {
                files: [],
                current: 'firstPage',
                previous: [],
                isLoading: false,
                isSuccess: false,
                isError: false,
                error: undefined,
            }
        }),
        resetTransferDetails: (state) => ({
            ...state, transfer: {
                isLoading: false,
                isSuccess: false,
                isError: false,
                error: undefined,
            }
        }),
        resetDeletedTransfer: (state) => ({
            ...state, deletedTransfer: {
                isSuccess: false,
                isLoading: false,
                isError: false,
            }
        })
    },
    extraReducers: (builder) => {
        builder
            .addCase(listTransfer.pending, (state) => {
                state.list.transfers = [];
                state.list.isLoading = true;
                state.list.isSuccess = false;
                state.list.isError = false;
                state.list.error = undefined;
            })
            .addCase(listTransfer.fulfilled, (state, action) => {
                state.list.transfers = action.payload.transfers;
                state.list.isSuccess = true;
                state.list.isLoading = false;

                if (action.payload.hasPrevious && state.list.next) { // next action
                    state.list.previous = [...state.list.previous, state.list.current];
                    state.list.current = state.list.next;
                }

                if (action.payload.hadNext) { // previous action
                    const current = state.list.previous.pop();
                    if (current) {
                        state.list.current = current;
                    }
                }

                if (action.payload.next) {
                    state.list.next = action.payload.next;
                } else { // last page
                    state.list.next = undefined;
                }
            })
            .addCase(listTransfer.rejected, (state, action) => {
                state.list.isSuccess = false;
                state.list.isLoading = false;
                state.list.isError = true;
                state.list.transfers = initialState.list.transfers;
                state.list.next = initialState.list.next;
                state.list.previous = initialState.list.previous;
                state.list.error = action.error;
            })
            // GET DETAILS
            .addCase(getTransferDetails.pending, (state) => {
                state.transferDetails.isLoading = true;
                state.transferDetails.isError = false;
                state.transferDetails.error = undefined;
            })
            .addCase(getTransferDetails.fulfilled, (state, action) => {
                state.transferDetails.isLoading = false;
                state.transferDetails.isSuccess = true;
                state.transferDetails.transfer = action.payload.transfer;
            })
            .addCase(getTransferDetails.rejected, (state, action) => {
                state.transferDetails.isLoading = false;
                state.transferDetails.isError = true;
                state.transferDetails.transfer = undefined;
                state.transferDetails.error = action.error;
            })
            // GET FILES
            .addCase(listTransferFiles.pending, (state) => {
                state.transferFiles.files = [];
                state.transferFiles.isLoading = true;
                state.transferFiles.isError = false;
                state.transferFiles.error = undefined;
            })
            .addCase(listTransferFiles.fulfilled, (state, action) => {
                state.transferFiles.isLoading = false;
                state.transferFiles.isSuccess = true;
                state.transferFiles.files = action.payload.files;

                if (action.payload.hasPrevious && state.transferFiles.next) { // next action
                    state.transferFiles.previous = [...state.transferFiles.previous, state.transferFiles.current];
                    state.transferFiles.current = state.transferFiles.next;
                }

                if (action.payload.hadNext) { // previous action
                    const current = state.transferFiles.previous.pop();
                    if (current) {
                        state.transferFiles.current = current;
                    }
                }

                if (action.payload.next) {
                    state.transferFiles.next = action.payload.next;
                } else { // last page
                    state.transferFiles.next = undefined;
                }
            })
            .addCase(listTransferFiles.rejected, (state, action) => {
                state.transferFiles.isLoading = false;
                state.transferFiles.isError = true;
                state.transferFiles.files = [];
                state.transferFiles.error = action.error;
            })
            // DELETE KEY
            .addCase(deleteTransfer.pending, (state) => {
                state.deletedTransfer.isLoading = true;
                state.deletedTransfer.isSuccess = false;
                state.deletedTransfer.isError = false;
                state.deletedTransfer.error = undefined;
            })
            .addCase(deleteTransfer.fulfilled, (state) => {
                state.deletedTransfer.isLoading = false;
                state.deletedTransfer.isSuccess = true;
                state.deletedTransfer.isError = false;
            })
            .addCase(deleteTransfer.rejected, (state, action) => {
                state.deletedTransfer.isLoading = false;
                state.deletedTransfer.isSuccess = false;
                state.deletedTransfer.isError = true;
                state.deletedTransfer.error = action.error;
            })
    }
});


export const { resetTransfer, resetDeletedTransfer, resetTransferFiles, resetTransferDetails } = transferSlice.actions;
export const transferState = (state: RootState) => state.transfer;

export default transferSlice.reducer;